import * as Sentry from "@sentry/browser";
import {
  Button,
  Table,
  message,
  Tag
} from "antd";
import Card from "../components/Card";
import { PageToolbar } from "../components/PageToolbar";
import { CheckCircleOutlined } from "@ant-design/icons";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
  ADVANCED_QUERY,
} from "../components/AdvancedSearchModal";
import ExportData from "../components/ExportData";
import { BULK_MOVE_CUSTOMER_TRANSACTION_TO_ISSUED, BULK_UPDATE_CUSTOMER_TRANSACTION_ALLOCATION_AMOUNT, ONLINE_TRANSACTIONS_QUERY, GET_ONLINE_TRANSACTION_STATE_ENUM, GET_ONLINE_TRANSACTION_SORT_FIELDS } from "../gql/online-transactions";
import {
  APIError,
  CustomerTransaction,
  Product,
  RowSelection,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  formatter,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  request,
  responseHandler,
  getStateColor,
  fetchEnumOptions
} from "../helpers/utilities";
import { ColumnType } from "antd/lib/table";
import ModalCreator from "../components/ModalCreator";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  clearAllFilters,
  hasActiveFilters,
  parseQueryParams,
  handleFieldRemove,
} from "../components/TableFilters";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface State {
  onlineTransactions: CustomerTransaction[];
  isLoading: boolean;
  isFiltering: boolean;
  query: string;
  filteredOnlineTransactions: CustomerTransaction[];
  selectedFieldsToDisplay: IField[];
  showAdvancedSearchModal: boolean;
  page: number;
  pageSize: number;
  onlineTransactionsCount: number;
  selectedIdsToExport: React.Key[];
  removedField?: IField;
  showConfirmationModal: boolean;
  showUpdateAllocationAmountConfirmationModal: boolean;
  q: string;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string; }>;
  selectedStates: string[];
  stateOptions: Array<{ value: string; label: string; color?: string; }>;
}

interface CustomerTransactionAndProduct extends CustomerTransaction {
  product?: Product;
}

export function getCustomerTransactionsColumns<T extends CustomerTransaction>(
  customerTransactions: CustomerTransaction[],
): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render(_: string, record: CustomerTransactionAndProduct): JSX.Element {
        return <span>{record.id}</span>;
      },
    },
    {
      title: "Customer",
      dataIndex: ["customer,fullname"],
      key: "customer",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        return (
          <NavLink
            to={{
              pathname: `/online-transactions/${record.id}`,
              state: { customerTransactions },
            }}
            data-cy="test-online-transactions-link"
          >
            {`${record.customer.petraId} | ${record.customer.fullname ??
              generateNameFromCustomer(record.customer)
              } | ${record.customer.ssnitNumber}`}
          </NavLink>
        );
      },
    },
    {
      title: "Product",
      dataIndex: ["customerSubscription,product,name"],
      key: "productName",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        const product = record.product || record.customerSubscription?.product;
        return (
          <NavLink
            to={{
              pathname: `/products/${product?.id}`,
            }}
          >
            {product?.name}
          </NavLink>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "transactionType",
      key: "transactionType",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        return <span>{capitalizeFirstLetter(record.transactionType)}</span>;
      },
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        return <span>{record.provider || "N/A"}</span>;
      },
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        const mode = record.mode ? capitalizeFirstLetter(formatSnakeCase(record.mode)) : "N/A";
        return <span>{mode}</span>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        return (
          <span>
            {record.createdAt ? moment(record.createdAt, "YYYY-MM-DD HH:mm:ss").format("llll") : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        return <span>{formatter.format(record.totalAmount)}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        return (
          <Tag
            color={getStateColor(record.status)}
            style={{ textTransform: "capitalize" }}
          >
            {formatSnakeCase(record.status)}
          </Tag>
        );
      },
    },
    {
      title: "TA Transaction Status",
      dataIndex: "taTransactionStatus",
      key: "taTransactionStatus",
      render(text: string, record: CustomerTransactionAndProduct): JSX.Element {
        return <Tag
          color={getStateColor(record?.taTransactionStatus || "N/A")}>{capitalizeFirstLetter(record?.taTransactionStatus || "N/A")}</Tag>;
      },
    },
  ];
}

export function OnlineTransactionTableFactory(
  data: CustomerTransaction[],
  rowSelection: RowSelection,
  isLoading: boolean,
  page?: number,
  count?: number,
  nextPage?: (page: number, pageSize: number) => void,
  pageSize?: number,
): JSX.Element {
  const tempData: CustomerTransaction[] = data.map(
    (transaction: CustomerTransaction): CustomerTransaction => {
      return { ...transaction, key: transaction.id };
    },
  );
  return (
    <Table
      loading={isLoading}
      scroll={{ x: 500 }}
      columns={getCustomerTransactionsColumns(data)}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: pageSize ?? 25,
        total: count,
        onChange: nextPage,
      }}
      rowSelection={rowSelection}
    />
  );
}

export default class AllOnlineTransactions extends React.Component<
  RouteComponentProps,
  State
> {
  private _isMounted = false;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      onlineTransactions: [],
      isLoading: true,
      isFiltering: false,
      query: "",
      filteredOnlineTransactions: [],
      selectedFieldsToDisplay: [],
      showAdvancedSearchModal: false,
      page: 1,
      onlineTransactionsCount: 0,
      selectedIdsToExport: [],
      q: "",
      pageSize: 25,
      showConfirmationModal: false,
      showUpdateAllocationAmountConfirmationModal: false,
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      selectedStates: [],
      stateOptions: [],
    };
    this.nextPage = this.nextPage.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
  }

  onSelectChange(newSelectedIdsToExport: React.Key[]) {
    this.setState({
      selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
        (item) => item,
      ),
    });
  }

  setQuery = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const query = e.target.value;
    e.persist();
    this.setState({ query, isFiltering: true });
    if (query.length === 0) {
      this.setState({ isFiltering: false });
      return;
    }
    const filtered: CustomerTransaction[] =
      this.state.onlineTransactions.filter(function (
        transaction: CustomerTransaction,
      ): boolean {
        if (
          transaction.customer?.fullname?.toLowerCase().includes(query) ||
          transaction.customer?.petraId?.toLowerCase().includes(query) ||
          (transaction as CustomerTransactionAndProduct).product?.name
            ?.toLowerCase()
            .includes(query) ||
          transaction.customerSubscription?.product?.name
            ?.toLowerCase()
            .includes(query) ||
          transaction?.transactionType?.toLowerCase().includes(query) ||
          transaction?.provider?.toLowerCase().includes(query) ||
          transaction?.mode?.toLowerCase().includes(query) ||
          transaction?.status?.toLowerCase().includes(query) ||
          transaction?.priceDate?.toLowerCase().includes(query)
        ) {
          return true;
        }
        return false;
      });
    this.setState({ filteredOnlineTransactions: filtered });
  };

  handleSortChange = (field: string, direction: string): void => {
    this.setState(
      { sortField: field, sortDirection: direction, page: 1 },
      () => {
        this.getOnlineTransactions();
      },
    );
  };

  getOnlineTransactions = async (): Promise<void> => {
    this.setState({ isLoading: true });
    const { page, q, pageSize, selectedStates, sortField, sortDirection, selectedFieldsToDisplay } = this.state;
    try {
      let results = [];
      let count = 0;

      if (selectedFieldsToDisplay.length > 0) {
        const fields = selectedFieldsToDisplay.map((f: IField) => ({
          field: f.name,
          operator: f.operator,
          values: f.hasBetween ? [f.value, f.betweenValue] : [f.value],
        }));

        const r: AxiosResponse = await request({
          query: ADVANCED_QUERY,
          variables: {
            model: "customerTransaction",
            fields,
            page,
            numberPerPage: pageSize,
          },
        });

        const advancedData = r.data?.data?.advancedQuery || {};
        results = advancedData.results || [];
        count = advancedData.count || 0;
      } else {
        const r: AxiosResponse = await request({
          query: ONLINE_TRANSACTIONS_QUERY,
          variables: {
            page,
            q,
            numberPerPage: pageSize,
            states: selectedStates.length > 0 ? selectedStates : null,
            orderBy: {
              field: sortField,
              direction: sortDirection,
            },
          },
        });

        const searchData = r.data?.data?.onlineTransactions;
        results = searchData.results || [];
        count = searchData.count;
      }

      this.setState({
        onlineTransactions: results,
        onlineTransactionsCount: count,
      });
    } catch (error) {
      message.error(`Error fetching transactions - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.getOnlineTransactions();
  }, DEBOUNCE_TIME);

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      { isLoading: true, page: page, pageSize: pageSize },
      async () => {
        await this.getOnlineTransactions();
        this.setState({ isLoading: false });
      },
    );
  }

  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ): void => {
    this.setState({
      onlineTransactions: data.results as CustomerTransaction[],
      selectedFieldsToDisplay: fields,
      onlineTransactionsCount: data.count,
    });
  };

  handleOnAdvancedSearchError = (error: string): void => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    handleFieldRemove(this, field, "customerTransaction");
  };

  handleStateFilter = (newSelectedStates: string[]): void => {
    this.setState({ selectedStates: newSelectedStates, page: 1 }, () => {
      this.getOnlineTransactions();
    });
  };

  mergeWithLocalState = (callback?: () => void): void => {
    const parsed = parseQueryParams({
      includeSort: true,
    });

    this.setState(
      {
        q: parsed.q || "",
        page: parsed.page || 1,
        selectedStates: parsed.selectedStates || [],
        sortField: parsed.sortField || "CREATED_AT",
        sortDirection: parsed.sortDirection || "DESC",
      },
      callback,
    );
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    const savedFields = JSON.parse(localStorage.getItem("customerTransaction") || "[]");
    this.setState({ selectedFieldsToDisplay: savedFields });

    await Promise.all([
      this.fetchStateEnums(),
      this.fetchSortFieldOptions(),
    ]);

    this.mergeWithLocalState(() => {
      this.getOnlineTransactions();
    });
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  bulkMoveCustomerTransactionToIssued = async (): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      response: {
        message: string;
      };
      errors: APIError[];
    }

    try {
      const response: AxiosResponse = await request({
        query: BULK_MOVE_CUSTOMER_TRANSACTION_TO_ISSUED,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "bulkMoveCustomerTransactionToIssued",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        message.success(mutation.response.message);
      }
    } catch (error) {
      message.error(`Failed to update transactions - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false, showConfirmationModal: false });
    }
  };

  bulkUpdateCustomerTransactionAllocationAmount = async (): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      response: {
        message: string;
      };
      errors: APIError[];
    }

    try {
      const response: AxiosResponse = await request({
        query: BULK_UPDATE_CUSTOMER_TRANSACTION_ALLOCATION_AMOUNT,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "bulkUpdateCustomerTransactionAllocationAmount",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        message.success(mutation.response.message);
      }
    } catch (error) {
      message.error(`Failed to initiate customer transaction allocation amounts update - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false, showUpdateAllocationAmountConfirmationModal: false });
    }
  };

  toggleConfirmationModal = () => {
    this.setState((prevState) => ({
      showConfirmationModal: !prevState.showConfirmationModal
    }));
  };

  toggleUpdateAllocationAmountConfirmationModal = () => {
    this.setState((prevState) => ({
      showUpdateAllocationAmountConfirmationModal: !prevState.showUpdateAllocationAmountConfirmationModal
    }));
  };

  fetchStateEnums = async (): Promise<void> => {
    const stateOptions = await fetchEnumOptions(
      GET_ONLINE_TRANSACTION_STATE_ENUM,
      {
        includeColor: true,
        errorMsg: "Failed to fetch state enums",
      },
    );

    if (this._isMounted) {
      this.setState({ stateOptions });
    }
  };

  fetchSortFieldOptions = async (): Promise<void> => {
    const sortFieldOptions = await fetchEnumOptions(
      GET_ONLINE_TRANSACTION_SORT_FIELDS,
      {
        errorMsg: "Failed to fetch sort options",
      },
    );

    if (this._isMounted) {
      this.setState({ sortFieldOptions });
    }
  };

  render(): JSX.Element {
    const {
      isLoading,
      onlineTransactions,
      filteredOnlineTransactions,
      isFiltering,
      removedField,
      page,
      selectedFieldsToDisplay,
      selectedIdsToExport,
      onlineTransactionsCount,
      pageSize,
      showConfirmationModal,
      showUpdateAllocationAmountConfirmationModal,
      sortField,
      sortDirection,
      sortFieldOptions,
      stateOptions,
      selectedStates,
    } = this.state;

    // rowSelection object indicates the need for row selection
    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };


    return (
      <>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="customerTransaction"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
          activeFields={selectedFieldsToDisplay}
        />
          <Card>
            <div className="pt-table-container">
          <PageToolbar modelName="transactions"
            searchPlaceholder="Search by Petra Id, product Name, Provider, Status, Mode, Transaction type"
            searchValue={this.state.q}
            onSearchChange={this.handleSearchInput}
            advancedSearch={{
              onOpen: this.toggleAdvancedSearchModel,
              activeFields: selectedFieldsToDisplay,
              onFieldRemove: this.handleOnFieldRemove,
            }}
            actions={
              <ExportData
                model={"customerTransaction"}
                dataArray={
                  isFiltering
                    ? filteredOnlineTransactions
                    : onlineTransactions
                }
                size={"large"}
                customIds={selectedIdsToExport}
              />
            }
            totalCount={onlineTransactionsCount}
            currentDataCount={
              isFiltering
                ? filteredOnlineTransactions.length
                : onlineTransactions.length
            }
            page={page}
            pageSize={pageSize}
            selectedCount={selectedIdsToExport.length}
            primaryAction={
              <EntitledSection
                entitlement={entitlements.OnlineTransactions.edit}
              >
                <Button
                  onClick={this.toggleConfirmationModal}
                  size={"large"}
                  style={{
                    backgroundColor: "green",
                    color: "white",
                  }}
                >
                  Bulk Process Transactions
                </Button>
              </EntitledSection>
            }
            filters={
              <PageToolbar.Filters
                isFiltering={hasActiveFilters(this.state, { includeSort: true })}
                onClear={() => clearAllFilters(this, "customerTransaction")}
                configs={[
                  {
                    label: "Status",
                    options: stateOptions,
                    selectedValues: selectedStates,
                    onChange: this.handleStateFilter,
                    icon: <CheckCircleOutlined />,
                  },
                ]}
                sort={{
                  field: sortField,
                  direction: sortDirection,
                  options: sortFieldOptions,
                  onChange: this.handleSortChange,
                }}
              />
            }
          />
              {OnlineTransactionTableFactory(
                onlineTransactions || [],
                rowSelection,
                isLoading,
                this.state.page,
                this.state.onlineTransactionsCount,
                this.nextPage,
                pageSize,
              )}

              {showConfirmationModal &&
                ModalCreator({
                  showModal: showConfirmationModal,
                  onOkay: this.bulkMoveCustomerTransactionToIssued,
                  toggleModal: this.toggleConfirmationModal,
                  inscription:
                    "Are you sure you want to complete this action?",
                })}
              {showUpdateAllocationAmountConfirmationModal &&
                ModalCreator({
                  showModal: showUpdateAllocationAmountConfirmationModal,
                  onOkay: this.bulkUpdateCustomerTransactionAllocationAmount,
                  toggleModal: this.toggleUpdateAllocationAmountConfirmationModal,
                  inscription:
                    "Are you sure you want to complete this action?",
                })}
            </div>
          </Card>
      </>
    );
  }
}
